import { defineStore } from 'pinia'
import type { CustomStoreImplementationOptions, KaElementClickEvent, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { initAliveStoreIds, kapixContext, navigateTo, openUrl, remove, scrollInfo } from 'kapix-components-vue3'
import hautDePageMenuStoreCustom from './store.custom'
import { $translate } from '~/modules/i18n'

const storeName = 'hautDePageMenu'
const customImplement: CustomStoreImplementationOptions = hautDePageMenuStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function hautDePageMenuFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        // Page info,
        $title: $translate('haut-de-page.title', 'Haut de Page'),
        $description: $translate('haut-de-page.description', ''),
        $publishedTime: new Date('2023-02-13T07:42:17.631Z'),
        $modifiedTime: new Date('2023-07-25T10:05:45.775Z'),
        // Constants,
        $constants: {},
        // Data,
        $data: {
          toggleEntreprises: undefined as Nullable<boolean>,
          toggleExpert: undefined as Nullable<boolean>,
          toggleFormation: undefined as Nullable<boolean>
        },
        $info: { hautDePage: scrollInfo() },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      fondClickOnXs (event: KaElementClickEvent) {
        event.vm?.closeTooltip()
      },
      /* @ts-ignore: to allow override in your custom file */
      iconClickOnXs (event: KaElementClickEvent) {
        event.vm?.closeTooltip()
      },
      /* @ts-ignore: to allow override in your custom file */
      entreprisesClickOnXs () {
        this.$data.toggleEntreprises = true
        this.$data.toggleExpert = undefined
        this.$data.toggleFormation = undefined
      },
      /* @ts-ignore: to allow override in your custom file */
      entreprisesClickOnXsWhenSelected () {
        this.$data.toggleEntreprises = false
      },
      /* @ts-ignore: to allow override in your custom file */
      expertClickOnXs () {
        this.$data.toggleExpert = true
        this.$data.toggleEntreprises = undefined
        this.$data.toggleFormation = undefined
      },
      /* @ts-ignore: to allow override in your custom file */
      expertClickOnXsWhenSelected () {
        this.$data.toggleExpert = false
        this.$data.toggleEntreprises = undefined
      },
      /* @ts-ignore: to allow override in your custom file */
      async pourquoiDevenirExpertClickOnXs () {
        await navigateTo({ name: 'hub-expert' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async commentCaMarcheClickOnXs () {
        await navigateTo({ name: 'hub-expert-Fonctionnement' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async textClickOnXs () {
        await navigateTo({ name: 'hub-expert-Ressources' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async contactClickOnXs () {
        await navigateTo({ name: 'Infos' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async text1ClickOnXs () {
        await navigateTo({ name: 'hub-entreprise' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async text3ClickOnXs () {
        await navigateTo({ name: 'hub-entreprise-Fonctionnement' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async text4ClickOnXs () {
        await navigateTo({ name: 'Blog' })
      },
      /* @ts-ignore: to allow override in your custom file */
      text5ClickOnXs () {
        openUrl('/experts', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      formationClickOnXs () {
        this.$data.toggleExpert = false
        this.$data.toggleEntreprises = undefined
        this.$data.toggleFormation = true
      },
      /* @ts-ignore: to allow override in your custom file */
      formationClickOnXsWhenSelected () {
        this.$data.toggleExpert = false
        this.$data.toggleEntreprises = undefined
        this.$data.toggleFormation = undefined
      },
      /* @ts-ignore: to allow override in your custom file */
      async ctaClickOnXs () {
        await navigateTo({ name: 'Contact' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async text6ClickOnXs () {
        await navigateTo({ name: 'Formations' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async tarifsClickOnXs () {
        await navigateTo({ name: 'Financement' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async text7ClickOnXs () {
        await navigateTo({ name: 'Pedagogie' })
      },
      /* @ts-ignore: to allow override in your custom file */
      headerClickOnXs (event: KaElementClickEvent) {
        event.vm?.closeTooltip()
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return hautDePageMenuFactory(storeId)()
}

export const hautDePageMenuRaw = hautDePageMenuFactory()
export const hautDePageMenu = () => hautDePageMenuRaw() as MergeCustomStore<typeof hautDePageMenuStoreCustom.instance>
